import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert, Box, Button, CircularProgress, LinearProgress, Stack, Tooltip, Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RawSurfaceChart from "./RawSurfaceChart";
import RawSurfaceDrilldown from "./RawSurfaceDrilldown";
import RawSurfaceRangePopover from "./RawSurfaceRangePopover";
import ClipShiftChart from "./ClipShiftChart";
import { tokens } from "../../theme";
import { openCircle } from "../../reducer/RawSurfaceSlice";
import { getFilterRejectReasons } from "./shaftRanges";

const PAN_ZOOM_HELP = (
    <>
        Scroll or swipe with two fingers to pan. Hold Ctrl and scroll (or pinch) to zoom. Hold
        Space and drag for fine control.
        <br /><br />
        Windows touchpad gestures vary by device/driver — if pinch-to-zoom doesn&apos;t do
        anything, hold Ctrl and scroll with two fingers instead.
    </>
);

/**
 * Main pane of the Raw Surface Analysis tab: the per-circle plot and drill-down.
 *
 * Ingest (Alignment Unit / Raw Capture pickers, uploading a new capture) lives in
 * RawSurfaceToolbar, directly under the tab strip - this component only renders once a capture
 * is loaded, or a splash prompting to use that toolbar if nothing is loaded yet.
 */
export default function RawSurfaceContent() {
    const rawSurface = useSelector((state) => state.rawSurface);
    const dispatch = useDispatch();

    const fitting = rawSurface.status === "fitting";
    const [pendingRange, setPendingRange] = useState(null);

    const filterRejectReasons = useMemo(
        () => getFilterRejectReasons(rawSurface.filterTrace),
        [rawSurface.filterTrace]
    );

    // Merged in here (not read directly off rawSurface.circles by the charts) so every chart
    // shows a consistent, single-source-of-truth reject reason without each one re-deriving it.
    const circles = useMemo(
        () => rawSurface.circles.map((c) => ({
            ...c, filterRejectReason: filterRejectReasons.get(c.x) ?? null,
        })),
        [rawSurface.circles, filterRejectReasons]
    );

    // Same combined kept/rejected definition as RawSurfaceSettings.js's Center Points Summary
    // card - counts circle-fix/gating rejects and NoiseReductionScatter/FocusRemoveWorst rejects
    // together, not just the former the way rawSurface.kept/dropped (the server's /fit response
    // counts) do, so the caption below and that card can't disagree.
    const keptCount = useMemo(
        () => circles.filter((c) => c.kept && !c.filterRejectReason && c.pointCount > 0).length,
        [circles]
    );
    const rejectedCount = useMemo(
        () => circles.filter((c) => c.pointCount > 0 && (!c.kept || c.filterRejectReason)).length,
        [circles]
    );

    // Refs to all three charts' imperative handles (resetView/setXRange - see RawSurfaceChart.js),
    // so the shared "Reset view" control and cross-chart X-window sync below can reach into each
    // Chart.js instance without lifting the pan/zoom view itself into Redux or component state -
    // it's purely a chart-rendering concern, not app state anything else needs to read.
    const chartRefs = useRef([]);
    chartRefs.current = [];
    const registerChartRef = (el) => {
        if (el) chartRefs.current.push(el);
    };

    // Any one chart's pan/zoom reports its new X window here; applied to the other two so all
    // three stay showing the same shaft-position window (they share one physical X domain, which
    // is the reason to view them stacked together at all).
    const handleXRangeChange = (min, max) => {
        chartRefs.current.forEach((chart) => chart?.setXRange(min, max));
    };

    const resetAllViews = () => {
        chartRefs.current.forEach((chart) => chart?.resetView());
    };

    // Safety net for a real flakiness gap that shows up on every browser, just via a different
    // event: each chart's own zoom handling (useChartPanZoom.js) is attached only to that chart's
    // own canvas, so if a pinch/ctrl-scroll's first frame lands just off it - the padding around
    // a chart card, the gap between the three stacked charts, the legend row - the per-chart
    // handler never runs, its preventDefault never fires, and the browser's own native zoom
    // kicks in instead: Safari fires gesturestart/gesturechange (WebKit-only) for a trackpad
    // pinch, while Chromium/Firefox instead fire a ctrlKey wheel event and zoom the whole page on
    // ctrl+scroll/pinch if nothing calls preventDefault. Capture-phase listeners here that
    // unconditionally preventDefault both paths (while this tab is mounted) close the gap
    // regardless of exactly where on the page a pinch/ctrl-scroll starts. Neither interferes with
    // the per-chart handlers below: preventDefault doesn't stop propagation, so their own
    // bubble-phase listeners still fire normally whenever the gesture is actually over a chart.
    useEffect(() => {
        const preventPageZoom = (e) => e.preventDefault();
        const preventCtrlWheelZoom = (e) => {
            if (e.ctrlKey) e.preventDefault();
        };
        document.addEventListener("gesturestart", preventPageZoom, { capture: true });
        document.addEventListener("gesturechange", preventPageZoom, { capture: true });
        document.addEventListener("gestureend", preventPageZoom, { capture: true });
        document.addEventListener("wheel", preventCtrlWheelZoom, { capture: true, passive: false });
        return () => {
            document.removeEventListener("gesturestart", preventPageZoom, { capture: true });
            document.removeEventListener("gesturechange", preventPageZoom, { capture: true });
            document.removeEventListener("gestureend", preventPageZoom, { capture: true });
            document.removeEventListener("wheel", preventCtrlWheelZoom, { capture: true, passive: false });
        };
    }, []);

    const renderSplash = () => (
        <Box sx={{ padding: "50px", textAlign: "center" }}>
            <Typography variant="h6" gutterBottom>Select an Alignment Unit and Raw Capture to View</Typography>
            <Typography variant="body2" color="text.secondary">
                Pick an Alignment Unit and a previously-uploaded capture above, or upload a new{" "}
                <code>saveRaw=1</code> capture from HardwareInterfaces - the pre-circle-fit laser
                profile, the same file GraphApp reads.
            </Typography>
        </Box>
    );

    // Selecting an existing capture from the toolbar's dropdown (loadCapture) sets status to
    // "fitting" the same as a threshold re-fit, but there are no charts up yet for the in-chart
    // overlay below to appear over - without this, that state showed nothing but the splash text.
    const renderLoadingCapture = () => (
        <Box sx={{ padding: "50px", textAlign: "center" }}>
            <Stack alignItems="center" spacing={1.5}>
                <CircularProgress size={28} thickness={3} color="secondary" />
                <Typography variant="body2" color="text.secondary">Loading capture&hellip;</Typography>
            </Stack>
        </Box>
    );

    const onCircleSelect = (circle) => dispatch(openCircle(circle.x));


    const renderAnalysis = () => (
        // No top padding - the right rail's first card sits flush at the grid cell's top edge
        // (RawSurfaceSettings.js has none either), so the charts need to start at the same spot
        // to stay top-aligned with it.
        <Stack spacing={1}>
            {/* Three stacked charts (Y, Z, Radius), each its own Chart.js instance - not one
                chart switched via a metric dropdown, per the approved mockup. */}
            <Box sx={{ position: "relative" }}>
                {fitting && (
                    <Box sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 30,
                        borderRadius: `${tokens.radius}px`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: alpha(tokens.ink, 0.72),
                        backdropFilter: "blur(1.5px)",
                    }}>
                        <Stack alignItems="center" spacing={1.25}>
                            <CircularProgress size={26} thickness={3} color="secondary" />
                            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: tokens.text }}>
                                Updating fit&hellip;
                            </Typography>
                        </Stack>
                    </Box>
                )}
                <Stack spacing={1.5}>
                    <RawSurfaceChart
                        ref={registerChartRef}
                        circles={circles}
                        metric="centerY"
                        title="Y — Vertical Center"
                        onSelect={onCircleSelect}
                        onRangeSelected={setPendingRange}
                        onXRangeChange={handleXRangeChange}
                        machineRange={rawSurface.machineRange}
                        motorRange={rawSurface.motorRange}
                        showRejected={rawSurface.showRejected}
                    />
                    <RawSurfaceChart
                        ref={registerChartRef}
                        circles={circles}
                        metric="centerZ"
                        title="Z — Horizontal Center"
                        onSelect={onCircleSelect}
                        onRangeSelected={setPendingRange}
                        onXRangeChange={handleXRangeChange}
                        machineRange={rawSurface.machineRange}
                        motorRange={rawSurface.motorRange}
                        showRejected={rawSurface.showRejected}
                    />
                    <RawSurfaceChart
                        ref={registerChartRef}
                        circles={circles}
                        metric="radius"
                        title="Radius"
                        onSelect={onCircleSelect}
                        onRangeSelected={setPendingRange}
                        onXRangeChange={handleXRangeChange}
                        machineRange={rawSurface.machineRange}
                        motorRange={rawSurface.motorRange}
                        showRejected={rawSurface.showRejected}
                    />
                </Stack>
            </Box>

            {/* Shared legend + circle-count caption, once for all three charts - matches the
                mockup's .legend/.decim-caption rows rather than each chart carrying its own. */}
            <Box sx={{
                display: "flex", gap: "14px", alignItems: "center",
                fontSize: "11.5px", color: tokens.muted, padding: "2px 4px",
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: tokens.kept }} />
                    Kept
                </Box>
                {/* Only shown when the "Show rejected" toggle (Fit Summary card) is on - otherwise
                    there's nothing on the chart for this swatch to represent. */}
                {rawSurface.showRejected && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: tokens.reject }} />
                        Rejected
                    </Box>
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: tokens.machine }} />
                    Machine
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: tokens.motor }} />
                    Motor
                </Box>
                <Typography sx={{ marginLeft: "auto", color: tokens.muted2, fontSize: "inherit" }}>
                    Click a point to inspect &middot; drag to set Machine/Motor range
                </Typography>
                <Tooltip title={PAN_ZOOM_HELP} arrow placement="top">
                    <InfoOutlinedIcon sx={{ fontSize: 15, color: tokens.muted, cursor: "help" }} />
                </Tooltip>
                <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    onClick={resetAllViews}
                    startIcon={<RestartAltIcon sx={{ fontSize: "16px !important" }} />}
                    sx={{
                        py: "2px",
                        px: "10px",
                        fontSize: "11.5px",
                        lineHeight: 1.6,
                        borderColor: tokens.line,
                        color: tokens.textSoft,
                        "&:hover": { borderColor: tokens.muted, backgroundColor: tokens.surface2 },
                    }}
                >
                    Reset view
                </Button>
            </Box>
            {/* The mockup's caption reads "Showing X of Y circles" because its own chart
                decimates for display; this one deliberately never does (see RawSurfaceChart's
                doc comment), so "X of Y" would always be "N of N". The real distinction worth
                surfacing here instead is kept vs. dropped - a bare total reads as "everything's
                fine" even when the current thresholds have dropped a meaningful share of them. */}
            <Typography sx={{ textAlign: "center", fontSize: "11.5px", color: tokens.muted, padding: "2px 4px 0" }}>
                <Box component="span" sx={{ color: tokens.kept }}>{keptCount.toLocaleString()}</Box>
                {" kept, "}
                <Box component="span" sx={{ color: tokens.reject }}>{rejectedCount.toLocaleString()}</Box>
                {" dropped "}
                &mdash;{" "}
                <Box component="span" sx={{ color: tokens.textSoft }}>
                    {rawSurface.circles.length.toLocaleString()}
                </Box>
                {" circles"}
            </Typography>

            {/* Only renders when clipping is on and there is something to compare. */}
            <ClipShiftChart />

        </Stack>
    );

    return (
        <Box>
            {rawSurface.status === "uploading" && (
                <Box sx={{ p: 1 }}>
                    <LinearProgress />
                    <Typography variant="caption" color="text.secondary">
                        Compressing and uploading capture...
                    </Typography>
                </Box>
            )}

            {rawSurface.error && (
                <Alert severity="error" sx={{ m: 1 }}>{rawSurface.error}</Alert>
            )}

            {rawSurface.circles.length > 0
                ? renderAnalysis()
                : (fitting ? renderLoadingCapture() : renderSplash())}

            <RawSurfaceDrilldown />
            <RawSurfaceRangePopover pending={pendingRange} onClose={() => setPendingRange(null)} />
        </Box>
    );
}
